import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FavoritesStatus, Tab, TabManagerContextType } from '../../../types/tabs';
import { favoriteTabsService, UserFavoriteTab } from '../../../services/favoriteTabsService';
import { useAuth } from '../../../features/shared/context/AuthContext';
import { usePermissions } from './PermissionContext';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { canAccessRoutePath, resolveDefaultAccessiblePath } from '../utils/getDefaultAccessiblePath';
import {
  isWorkspaceRoute,
  normalizeAccessibleFavorites,
  resolveWorkspaceEntryPath,
  shouldAwaitFavoritesForEntry,
} from '../utils/resolveWorkspaceEntryPath';
import { fromPersistedTab, parsePersistedTabState, toPersistedTab } from './tabPersistence';
import { clearPanePersistedFiltersForPath } from '../hooks/usePanePersistedActiveFilters';
import { clearFormDraftsForPanePath } from '../hooks/useFormDialogInit';
import { isTabKeepAliveEnabled, MAX_OPEN_TABS } from '../config/tabKeepAlive';
import { enforceOpenTabLimit } from './enforceOpenTabLimit';
import { buildManualPath } from '../../manual/utils/manualTopicPersistence';
import { applyPinnedOrder } from '../utils/latestOnlyWriter';
import { createPinnedOrderSync } from '../utils/navigationSync';

import { confirmTabClosures } from './tabCloseGuards';

export { toPersistedTab } from './tabPersistence';

const TabManagerContext = createContext<TabManagerContextType | undefined>(undefined);

const ROUTE_LABELS: Record<string, string> = {
  '/production': 'Produção',
  '/production/planning': 'Ordens',
  '/production/planning/ordens': 'Ordens de Produção',
  '/production/planning/processos': 'Processos',
  '/production/schedule': 'Sequenciamento',
  '/production/resources': 'Parametrização',
  '/production/apontamento': 'Apontamento',
  '/production/control': 'Controle de Produção',
  '/production/control/auditoria': 'Auditoria',
  '/production/control/nao-conformidades': 'Não Conformidades',
  '/production/control/lotes': 'Lotes',
  '/production/control/monitoramento': 'Monitoramento',
  '/production/monitoring': 'Monitoramento',
  '/production/factory-view': 'Visão da Fábrica',
  '/production/dashboards': 'Dashboards',
  '/production/melhoria': 'Melhoria',
  '/production/control/relatorios': 'Relatórios',
  '/cadastros/parametrizacao': 'Parametrização',
  '/cadastros/parametrizacao/setores': 'Setores',
  '/cadastros/parametrizacao/maquinas': 'Máquinas',
  '/cadastros/parametrizacao/categorias': 'Categorias',
  '/cadastros/parametrizacao/causas': 'Causas',
  '/quality': 'Qualidade',
  '/quality/parametros': 'Parâmetros',
  '/quality/checklist': 'Checklist',
  '/quality/regras': 'Regras',
  '/quality/auditoria': 'Auditoria',
  '/quality/dashboards': 'Dashboards',
  '/quality/planejamento': 'Planejamento',
  '/quality/operacao': 'Operação',
  '/cadastros/parametrizacao/horarios': 'Horários',
  '/maintenance/control': 'Controle de Manutenção',
  '/maintenance/control/estoque': 'Manutenção — Estoque',
  '/maintenance/control/checklist': 'Manutenção — Checklist',
  '/maintenance/control/historico': 'Manutenção — Histórico',
  '/maintenance/planning': 'Planejamento de Manutenção',
  '/maintenance/dashboards': 'Painel de Manutenção',
  '/maintenance/control/machine': 'Detalhes da Máquina',
  '/maintenance/parametrizacao/sensores': 'Sensores',
  '/sensoriamento': 'Sensoriamento',
  '/sensoriamento/sensores': 'Sensores',
  '/sensoriamento/relatorios': 'Relatórios de Sensoriamento',
  '/sensoriamento/ativacao': 'Ativar Dispositivo',
  '/product-engineering': 'Engenharia',
  '/product-engineering/eng-produto': 'Eng. Produto',
  '/product-engineering/componentes': 'Componentes',
  '/product-engineering/processos': 'Processos',
  '/product-engineering/eng-produto/montagem': 'Montagem',
  '/cadastros': 'Cadastros',
  '/cadastros/categoria': 'Categoria',
  '/cadastros/produto': 'Produto',
  '/cadastros/itens-consumo': 'Itens de Consumo',
  '/cadastros/clientes': 'Clientes',
  '/cadastros/formas-pagamento': 'Formas de pagamento',
  '/suprimentos': 'Suprimentos',
  '/suprimentos/estoque': 'Estoque',
  '/suprimentos/separacoes': 'Separações',
  '/suprimentos/movimentacoes': 'Movimentações',
  '/suprimentos/parametrizacao': 'Parametrização',
  '/suprimentos/notas-fiscais': 'Notas Fiscais',
  '/sales': 'Vendas',
  '/sales/pdv': 'PDV',
  '/sales/pedidos': 'Pedidos',
  '/sales/tabela': 'Tabela de Vendas',
  '/admin-panel': 'Painel Admin',
  '/sensores': 'Sensores',
  '/settings': 'Configurações',
  '/test': 'Teste',
  '/manual': 'Manual',
  '/profile': 'Perfil',
};

const getTabLabelForPath = (path: string): string =>
  ROUTE_LABELS[path] ||
  (path.startsWith('/product-engineering/eng-produto/montagem/') ? 'Montagem' : 'Página');

const resolveTabLabel = (path: string, storedLabel?: string): string =>
  ROUTE_LABELS[path] ?? storedLabel ?? getTabLabelForPath(path);

const createPlaceholderTab = (
  path: string,
  options?: {
    label?: string;
    isClosable?: boolean;
    isPinned?: boolean;
    lastAccessed?: Date;
  }
): Tab => ({
  id: path,
  label: options?.label ?? resolveTabLabel(path),
  path,
  isClosable: options?.isClosable ?? true,
  isPinned: options?.isPinned ?? false,
  lastAccessed: options?.lastAccessed ?? new Date(),
  component: () => null,
});

const dedupeTabsByPath = (tabs: Tab[]): Tab[] => {
  const orderedPaths: string[] = [];
  const byPath = new Map<string, Tab>();

  for (const tab of tabs) {
    if (!tab.path) {
      continue;
    }

    const normalized: Tab = {
      ...tab,
      id: tab.path,
      label: resolveTabLabel(tab.path, tab.label),
      lastAccessed: tab.lastAccessed instanceof Date ? tab.lastAccessed : new Date(tab.lastAccessed),
      component: tab.component ?? (() => null),
    };

    if (!byPath.has(normalized.path)) {
      orderedPaths.push(normalized.path);
      byPath.set(normalized.path, normalized);
      continue;
    }

    const existing = byPath.get(normalized.path)!;
    byPath.set(normalized.path, {
      ...existing,
      ...normalized,
      label: resolveTabLabel(normalized.path, existing.label || normalized.label),
      isPinned: existing.isPinned || normalized.isPinned,
      lastAccessed:
        existing.lastAccessed > normalized.lastAccessed ? existing.lastAccessed : normalized.lastAccessed,
    });
  }

  return orderedPaths.map((path) => byPath.get(path)!);
};

const resolveActiveTabId = (tabs: Tab[], requestedActiveTabId: string | null): string | null => {
  if (requestedActiveTabId && tabs.some((tab) => tab.id === requestedActiveTabId)) {
    return requestedActiveTabId;
  }

  return tabs[0]?.id ?? null;
};

const upsertWorkspaceTab = (tabs: Tab[], incomingTab: Tab): Tab[] => {
  const existing = tabs.find((tab) => tab.path === incomingTab.path);

  if (!existing) {
    return dedupeTabsByPath([...tabs, incomingTab]);
  }

  return tabs.map((tab) =>
    tab.path === incomingTab.path
      ? {
          ...tab,
          isPinned: tab.isPinned || incomingTab.isPinned,
          isClosable: incomingTab.isClosable ?? tab.isClosable,
          label: resolveTabLabel(tab.path, tab.label || incomingTab.label),
          lastAccessed:
            tab.lastAccessed > incomingTab.lastAccessed ? tab.lastAccessed : incomingTab.lastAccessed,
        }
      : tab
  );
};

const reconcileWorkspaceWithRemoteFavorites = (
  currentTabs: Tab[],
  remoteFavorites: UserFavoriteTab[]
): Tab[] => {
  const favoritePaths = new Set(remoteFavorites.map((favorite) => favorite.path));

  const merged = dedupeTabsByPath(currentTabs).map((tab) => {
    const remoteFavorite = remoteFavorites.find((favorite) => favorite.path === tab.path);
    return {
      ...tab,
      isPinned: favoritePaths.has(tab.path),
      label: resolveTabLabel(tab.path, remoteFavorite?.label ?? tab.label),
    };
  });

  const existingPaths = new Set(merged.map((tab) => tab.path));
  const missingFavorites = remoteFavorites
    .filter((favorite) => !existingPaths.has(favorite.path))
    .map((favorite) =>
      createPlaceholderTab(favorite.path, {
        label: resolveTabLabel(favorite.path, favorite.label),
        isPinned: true,
      })
    );

  return [...merged, ...missingFavorites];
};

const forgetRemovedTabs = (previous: Tab[], next: Tab[]): Tab[] => {
  if (previous.length === 0 || previous === next) return next;
  const nextPaths = new Set(next.map((tab) => tab.path));
  for (const tab of previous) {
    if (!nextPaths.has(tab.path)) {
      clearPanePersistedFiltersForPath(tab.path);
      clearFormDraftsForPanePath(tab.path);
    }
  }
  return next;
};

const limitOpenTabs = (tabs: Tab[], protectPath?: string | null): Tab[] =>
  enforceOpenTabLimit(tabs, protectPath, MAX_OPEN_TABS);

export const useTabManager = () => {
  const context = useContext(TabManagerContext);
  if (!context) {
    console.error('useTabManager deve ser usado dentro de TabManagerProvider');
    throw new Error('useTabManager deve ser usado dentro de TabManagerProvider');
  }
  return context;
};

interface TabManagerProviderProps {
  children: ReactNode;
}

const TAB_STORAGE_KEY = 'sistemat-tabs';
const TAB_PERSIST_DEBOUNCE_MS = 250;
const FAVORITES_ENTRY_BOOT_TIMEOUT_MS = 8_000;
const FAVORITES_LOAD_TIMEOUT_MS = 12_000;
const FAVORITES_RETRY_DELAY_MS = 30_000;
const MAX_FAVORITES_RETRIES = 1;

export const TabManagerProvider: React.FC<TabManagerProviderProps> = ({ children }) => {
  const [workspaceTabs, setWorkspaceTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [favoriteTabsMap, setFavoriteTabsMap] = useState<Map<string, UserFavoriteTab>>(new Map());
  // Sincronização em andamento do favorito de cada path (ver reconcileFavorite); a
  // reordenação espera por ela em vez de mandar uma lista sem o id novo.
  const favoriteSyncRef = useRef(new Map<string, Promise<void>>());
  // Última intenção do usuário por path (fixar = true, desfixar = false): é o alvo para
  // onde o favorito do servidor converge, qualquer que seja a ordem das respostas.
  const pinIntentRef = useRef(new Map<string, boolean>());
  // Geração da sessão: muda a cada troca de usuário/logout; respostas de outra
  // geração são descartadas (não mexem no estado nem disparam requisições).
  const sessionRef = useRef(0);
  // Contador de cliques de fixar/desfixar e o último clique de cada path: uma lista
  // remota pedida antes do clique não decide o estado desse path.
  const pinChangeSeqRef = useRef(0);
  const pinChangedAtRef = useRef(new Map<string, number>());
  // Lista remota → momento (pinChangeSeqRef) em que foi pedida.
  const remoteRequestedAtRef = useRef(new WeakMap<object, number>());
  const favoriteTabsMapRef = useRef(favoriteTabsMap);
  favoriteTabsMapRef.current = favoriteTabsMap;
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [favoritesStatus, setFavoritesStatus] = useState<FavoritesStatus>('idle');
  const [favoritesRetryNonce, setFavoritesRetryNonce] = useState(0);
  const [resolvedEntryPath, setResolvedEntryPath] = useState<string | null>(null);
  const hasHydratedWorkspaceRef = useRef(false);
  const entryPathCommittedRef = useRef(false);
  const hasSyncedFavoritesRef = useRef(false);
  const isFetchingFavoritesRef = useRef(false);
  const hasFailedAuthRef = useRef(false);
  const favoritesRetryCountRef = useRef(0);
  const favoritesRetryTimeoutRef = useRef<number | null>(null);
  const previousUserIdRef = useRef<string | null>(null);
  const protectPathRef = useRef<string>('/');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { permissions, hasPermission, isLoading: permissionsLoading } = usePermissions();
  const permissionsKey = permissions.join('|');
  const { isSuperAdmin } = useAccessControl();
  protectPathRef.current = location.pathname;
  const tabs = workspaceTabs;

  const defaultAccessiblePath = React.useMemo(
    () => resolveDefaultAccessiblePath(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin],
  );
  const canAccessFavoritePath = React.useCallback(
    (path: string) => canAccessRoutePath(path, (permission) => permissions.includes(permission), isSuperAdmin),
    [permissions, isSuperAdmin],
  );
  // Permissões vigentes quando a resposta chega (não quando o GET começou): uma
  // revogação durante a requisição não pode trazer de volta a aba que ela removeu.
  const canAccessFavoritePathRef = useRef(canAccessFavoritePath);
  canAccessFavoritePathRef.current = canAccessFavoritePath;

  const fetchNormalizedFavorites = React.useCallback(
    async (timeoutMs: number): Promise<UserFavoriteTab[]> => {
      const favorites = await Promise.race([
        favoriteTabsService.getFavorites(),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error('FAVORITES_LOAD_TIMEOUT')), timeoutMs);
        }),
      ]);
      return normalizeAccessibleFavorites(favorites, canAccessFavoritePathRef.current);
    },
    [],
  );

  /**
   * Junta a lista remota com o que o usuário fez depois de pedi-la: para um path com
   * sincronização em andamento ou clicado depois do pedido, vale o favorito já conhecido
   * localmente (mapa) e a intenção do usuário (tela).
   */
  const mergeRemoteFavorites = React.useCallback((remote: UserFavoriteTab[], requestedAt: number) => {
    const isLocal = (path: string) =>
      favoriteSyncRef.current.has(path) || (pinChangedAtRef.current.get(path) ?? 0) > requestedAt;
    const map = new Map<string, UserFavoriteTab>();
    remote.forEach((favorite) => { if (!isLocal(favorite.path)) map.set(favorite.path, favorite); });
    favoriteTabsMapRef.current.forEach((favorite, path) => { if (isLocal(path)) map.set(path, favorite); });
    // Para a tela: favoritos remotos, menos o que o usuário desfixou, mais o que fixou.
    const pinned = remote.filter((favorite) => !isLocal(favorite.path) || pinIntentRef.current.get(favorite.path));
    pinIntentRef.current.forEach((wantPinned, path) => {
      if (!wantPinned || !isLocal(path) || pinned.some((favorite) => favorite.path === path)) return;
      const known = map.get(path) ?? remote.find((favorite) => favorite.path === path);
      if (known) pinned.push(known);
    });
    return { map, pinned, isLocal };
  }, []);

  const applyFavoritesToWorkspace = React.useCallback((normalizedFavorites: UserFavoriteTab[], requestedAt: number) => {
    const { map, pinned, isLocal } = mergeRemoteFavorites(normalizedFavorites, requestedAt);
    favoriteTabsMapRef.current = map;
    setFavoriteTabsMap(map);
    setWorkspaceTabs((prev) => {
      // Fixadas localmente ainda sem favorito no servidor continuam fixadas.
      const localOnly = new Set(
        prev.filter((tab) => tab.isPinned && isLocal(tab.path) && pinIntentRef.current.get(tab.path) && !map.has(tab.path))
          .map((tab) => tab.path),
      );
      const reconciled = reconcileWorkspaceWithRemoteFavorites(prev, pinned)
        .map((tab) => (localOnly.has(tab.path) ? { ...tab, isPinned: true } : tab));
      return forgetRemovedTabs(prev, limitOpenTabs(reconciled, protectPathRef.current));
    });
  }, [mergeRemoteFavorites]);

  // Monitorar mudanças de usuário e limpar estado quando necessário
  useEffect(() => {
    const currentUserId = user?.id || null;
    
    // Se o usuário logou (era null e agora tem ID)
    if (previousUserIdRef.current === null && currentUserId !== null) {
      setIsBootstrapping(true);
      setIsBootstrapped(false);
      setFavoritesStatus('idle');
      favoritesRetryCountRef.current = 0;
      if (favoritesRetryTimeoutRef.current !== null) {
        window.clearTimeout(favoritesRetryTimeoutRef.current);
        favoritesRetryTimeoutRef.current = null;
      }
      hasHydratedWorkspaceRef.current = false;
      hasSyncedFavoritesRef.current = false;
      hasFailedAuthRef.current = false;
      entryPathCommittedRef.current = false;
      setResolvedEntryPath(null);
    }
    // Se o usuário mudou (tinha um ID e agora tem outro)
    else if (previousUserIdRef.current !== null && currentUserId !== null && previousUserIdRef.current !== currentUserId) {
      setWorkspaceTabs([]);
      setActiveTabId(null);
      setFavoriteTabsMap(new Map());
      setFavoritesStatus('idle');
      favoritesRetryCountRef.current = 0;
      if (favoritesRetryTimeoutRef.current !== null) {
        window.clearTimeout(favoritesRetryTimeoutRef.current);
        favoritesRetryTimeoutRef.current = null;
      }
      hasHydratedWorkspaceRef.current = false;
      hasSyncedFavoritesRef.current = false;
      hasFailedAuthRef.current = false;
      entryPathCommittedRef.current = false;
      setResolvedEntryPath(null);
      setIsBootstrapping(true);
      setIsBootstrapped(false);
      localStorage.removeItem(TAB_STORAGE_KEY);
    }
    // Se fez logout (usuário era válido e agora é null)
    else if (previousUserIdRef.current !== null && currentUserId === null) {
      setWorkspaceTabs([]);
      setActiveTabId(null);
      setFavoriteTabsMap(new Map());
      setFavoritesStatus('idle');
      favoritesRetryCountRef.current = 0;
      if (favoritesRetryTimeoutRef.current !== null) {
        window.clearTimeout(favoritesRetryTimeoutRef.current);
        favoritesRetryTimeoutRef.current = null;
      }
      hasHydratedWorkspaceRef.current = false;
      hasSyncedFavoritesRef.current = false;
      hasFailedAuthRef.current = false;
      entryPathCommittedRef.current = false;
      setResolvedEntryPath(null);
      setIsBootstrapping(false);
      setIsBootstrapped(false);
      localStorage.removeItem(TAB_STORAGE_KEY);
    }
    
    if (previousUserIdRef.current !== currentUserId) {
      sessionRef.current += 1;
      favoriteSyncRef.current.clear();
      pinIntentRef.current.clear();
      pinChangedAtRef.current.clear();
      // Um GET de favoritos da sessão anterior em voo não pode travar o da nova.
      isFetchingFavoritesRef.current = false;
      if (favoritesRetryTimeoutRef.current !== null) {
        window.clearTimeout(favoritesRetryTimeoutRef.current);
        favoritesRetryTimeoutRef.current = null;
      }
      favoriteTabsMapRef.current = new Map();
      syncPinnedOrderRef.current = null; // recriada na próxima renderização, já na nova sessão
    }
    previousUserIdRef.current = currentUserId;
  }, [user?.id]);

  useEffect(() => {
    return () => {
      if (favoritesRetryTimeoutRef.current !== null) {
        window.clearTimeout(favoritesRetryTimeoutRef.current);
      }
    };
  }, []);

  // Constantes para controle de memória
  const MAX_TAB_AGE_DAYS = 7;
  const CLEANUP_INTERVAL_MS = 60 * 60 * 1000; // 1 hora
  const TAB_SWITCH_CLEANUP_DELAY_MS = 5000; // 5 segundos
  const MIN_TAB_DATA_SIZE_TO_CLEAN = 100 * 1024; // 100KB

  // Limpar tabs antigas automaticamente
  const cleanupOldTabs = useCallback(() => {
    const now = Date.now();
    const maxAge = MAX_TAB_AGE_DAYS * 24 * 60 * 60 * 1000;
    
    setWorkspaceTabs(prev => {
      const cleaned = prev.filter(tab => {
        if (tab.isPinned) return true;
        const lastAccessed = tab.lastAccessed ? new Date(tab.lastAccessed).getTime() : 0;
        return (now - lastAccessed) < maxAge;
      });

      return forgetRemovedTabs(prev, limitOpenTabs(cleaned, activeTabId));
    });
  }, [activeTabId]);

  // Limpar dados de cache de tabs inativas (desligado com keep-alive — o componente mantém o estado)
  const cleanupInactiveTabData = useCallback(() => {
    if (isTabKeepAliveEnabled()) return;

    setWorkspaceTabs(prev => prev.map(tab => {
      if (tab.id !== activeTabId && tab.data) {
        try {
          const dataSize = JSON.stringify(tab.data).length;
          if (dataSize > MIN_TAB_DATA_SIZE_TO_CLEAN) {
            return { ...tab, data: undefined, isDirty: true };
          }
        } catch {
          return { ...tab, data: undefined, isDirty: true };
        }
      }
      return tab;
    }));
  }, [activeTabId, MIN_TAB_DATA_SIZE_TO_CLEAN]);

  // Limpeza periódica e quando necessário
  useEffect(() => {
    const interval = setInterval(() => {
      cleanupOldTabs();
      cleanupInactiveTabData();
    }, CLEANUP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [cleanupOldTabs, cleanupInactiveTabData, CLEANUP_INTERVAL_MS]);
  
  useEffect(() => {
    if (tabs.length > MAX_OPEN_TABS) {
      cleanupOldTabs();
    }
  }, [tabs.length, cleanupOldTabs]);

  useEffect(() => {
    const timeoutId = setTimeout(cleanupInactiveTabData, TAB_SWITCH_CLEANUP_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [activeTabId, cleanupInactiveTabData]);

  // Salvar abas no localStorage (com limite)
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (tabs.length === 0) {
        localStorage.removeItem(TAB_STORAGE_KEY);
        return;
      }

      try {
        // Persistir só metadados leves; cache/dados pesados não devem ir para storage.
        const tabsToSave = tabs.map(toPersistedTab);
        localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify({
          tabs: tabsToSave,
          activeTabId
        }));
      } catch (error) {
        console.error('Erro ao salvar abas:', error);
        try {
          const tabsToSave = limitOpenTabs(tabs, activeTabId).map(toPersistedTab);
          localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify({
            tabs: tabsToSave,
            activeTabId
          }));
        } catch {
          localStorage.removeItem(TAB_STORAGE_KEY);
        }
      }
    }, TAB_PERSIST_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [tabs, activeTabId]);

  // Abrir nova aba
  const openTab = useCallback((tabData: Omit<Tab, 'id' | 'lastAccessed'>) => {
    if (!canAccessRoutePath(tabData.path, hasPermission, isSuperAdmin)) {
      return;
    }

    const favorite = favoriteTabsMap.get(tabData.path);
    const newTab = createPlaceholderTab(tabData.path, {
      label: tabData.label,
      isClosable: tabData.isClosable,
      isPinned: favorite && pinIntentRef.current.get(tabData.path) !== false ? true : (tabData.isPinned ?? false),
      lastAccessed: new Date(),
    });

    setWorkspaceTabs((prev) => {
      const updated = upsertWorkspaceTab(prev, newTab);
      return forgetRemovedTabs(prev, limitOpenTabs(updated, newTab.path));
    });
    setActiveTabId(tabData.path);
  }, [favoriteTabsMap, hasPermission, isSuperAdmin]);

  const updateTabLabelForPath = useCallback((path: string, label: string) => {
    setWorkspaceTabs((prev) => prev.map((tab) => (tab.path === path ? { ...tab, label } : tab)));
  }, []);

  // Fechar aba
  const closeTab = useCallback((tabId: string) => {
    const tabToClose = tabs.find(tab => tab.id === tabId);
    if (!tabToClose || !confirmTabClosures([tabToClose.path])) return;

    clearPanePersistedFiltersForPath(tabToClose.path);
    clearFormDraftsForPanePath(tabToClose.path);
    setWorkspaceTabs(prev => prev.filter(tab => tab.id !== tabId));

    // Se fechou a aba ativa, ativa a próxima
    if (activeTabId === tabId) {
      const remainingTabs = tabs.filter(tab => tab.id !== tabId);
      if (remainingTabs.length > 0) {
        const nextTab = remainingTabs[remainingTabs.length - 1];
        setActiveTabId(nextTab.id);
        navigate(nextTab.path);
      } else {
        setActiveTabId(null);
        navigate(defaultAccessiblePath);
      }
    }
  }, [tabs, activeTabId, navigate, defaultAccessiblePath]);

  const closeTabAndNavigate = useCallback((tabIdToClose: string, nextPath: string) => {
    const tabToClose = tabs.find((tab) => tab.id === tabIdToClose);
    if (tabToClose && !confirmTabClosures([tabToClose.path])) return;
    if (tabToClose) {
      clearPanePersistedFiltersForPath(tabToClose.path);
      clearFormDraftsForPanePath(tabToClose.path);
    }
    setWorkspaceTabs((prev) => prev.filter((tab) => tab.id !== tabIdToClose));
    setActiveTabId((prevActive) => (prevActive === tabIdToClose ? nextPath : prevActive));
    navigate(nextPath);
  }, [navigate, tabs]);

  const removeTabByPath = useCallback((path: string) => {
    setWorkspaceTabs((prev) => {
      if (!prev.some((tab) => tab.path === path)) return prev;
      clearPanePersistedFiltersForPath(path);
      clearFormDraftsForPanePath(path);
      return prev.filter((tab) => tab.path !== path);
    });
    setActiveTabId((prevActive) => (prevActive === path ? null : prevActive));
  }, []);

  const pruneInaccessibleTabs = useCallback(() => {
    const checkAccess = (permission: string) => permissions.includes(permission);
    setWorkspaceTabs((prev) => {
      const next = prev.filter((tab) => canAccessRoutePath(tab.path, checkAccess, isSuperAdmin));
      if (next.length === prev.length) return prev;
      return next;
    });
    setActiveTabId((prevActive) => {
      if (!prevActive) return prevActive;
      const checkAccess = (permission: string) => permissions.includes(permission);
      return canAccessRoutePath(prevActive, checkAccess, isSuperAdmin) ? prevActive : null;
    });
  }, [permissions, isSuperAdmin]);

  const lastPrunedPermissionsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id || permissionsLoading) return;
    if (lastPrunedPermissionsKeyRef.current === permissionsKey) return;
    lastPrunedPermissionsKeyRef.current = permissionsKey;
    pruneInaccessibleTabs();
  }, [user?.id, permissionsLoading, permissionsKey, pruneInaccessibleTabs]);

  // Ativar aba
  const activateTab = useCallback((tabId: string) => {
    const tab = tabs.find(tab => tab.id === tabId);
    if (tab) {
      setActiveTabId(tabId);
      setWorkspaceTabs((prev) =>
        prev.map((candidate) =>
          candidate.id === tabId ? { ...candidate, lastAccessed: new Date() } : candidate,
        ),
      );
    }
  }, [tabs]);

  // Fechar outras abas
  const closeOtherTabs = useCallback((tabId: string) => {
    if (!confirmTabClosures(tabs.filter((tab) => tab.id !== tabId).map((tab) => tab.path))) return;
    setWorkspaceTabs(prev => prev.filter(tab => tab.id === tabId));
    setActiveTabId(tabId);
  }, [tabs]);

  // Fechar todas as abas
  const closeAllTabs = useCallback(() => {
    if (!confirmTabClosures(tabs.map((tab) => tab.path))) return;
    setWorkspaceTabs([]);
    setActiveTabId(null);
    navigate(defaultAccessiblePath);
  }, [defaultAccessiblePath, navigate, tabs]);

  const rememberFavorite = useCallback((path: string, favorite: UserFavoriteTab | null) => {
    const next = new Map(favoriteTabsMapRef.current);
    if (favorite) next.set(path, favorite);
    else next.delete(path);
    favoriteTabsMapRef.current = next;
    setFavoriteTabsMap(next);
  }, []);

  const applyPinnedState = useCallback((path: string, isPinned: boolean) => {
    setWorkspaceTabs((prev) => prev.map((candidate) =>
      candidate.path === path ? { ...candidate, isPinned } : candidate
    ));
  }, []);

  /**
   * Leva o favorito do servidor até a última intenção do usuário para o path. Uma
   * operação por path de cada vez (POST e DELETE nunca se cruzam); ao terminar, confere
   * a intenção de novo e faz o que faltar. Falha sem nova intenção: a tela volta ao que
   * o servidor tem. Troca de sessão interrompe tudo sem mexer no estado.
   */
  const reconcileFavorite = useCallback((path: string, label: string, orderIndex: number) => {
    if (favoriteSyncRef.current.has(path)) return; // o laço em andamento lê a intenção nova
    const session = sessionRef.current;
    const isCurrent = () => session === sessionRef.current;
    const run = (async () => {
      let lookedUp = false;
      while (isCurrent()) {
        const wantPinned = pinIntentRef.current.get(path);
        const known = favoriteTabsMapRef.current.get(path);
        if (wantPinned === undefined || wantPinned === Boolean(known)) {
          if (wantPinned || lookedUp) return;
          // Quer desfixado e não conhecemos o favorito: pode existir só no servidor.
          lookedUp = true;
          try {
            const found = (await favoriteTabsService.getFavorites()).find((fav) => fav.path === path);
            if (!isCurrent()) return;
            if (found) rememberFavorite(path, found);
          } catch (error) {
            console.error('Erro ao buscar favoritos:', error);
            if (!isCurrent()) return;
          }
          continue; // a intenção pode ter mudado durante o GET: o topo do laço decide
        }
        try {
          if (wantPinned) {
            const created = await favoriteTabsService.createFavorite({ path, label, order_index: orderIndex });
            if (!isCurrent()) return;
            rememberFavorite(path, created);
          } else if (known) {
            await favoriteTabsService.deleteFavorite(known.id);
            if (!isCurrent()) return;
            rememberFavorite(path, null);
            lookedUp = true; // exclusão confirmada: não há o que procurar no servidor
          }
        } catch (error) {
          console.error(wantPinned ? 'Erro ao salvar favorito:' : 'Erro ao remover favorito:', error);
          if (!isCurrent()) return;
          // O usuário mudou de ideia durante a requisição: o laço tenta a intenção nova.
          if (pinIntentRef.current.get(path) !== wantPinned) continue;
          const serverPinned = favoriteTabsMapRef.current.has(path);
          pinIntentRef.current.set(path, serverPinned);
          applyPinnedState(path, serverPinned);
          return;
        }
      }
    })();
    favoriteSyncRef.current.set(path, run);
    run.finally(() => {
      if (favoriteSyncRef.current.get(path) === run) favoriteSyncRef.current.delete(path);
    });
  }, [applyPinnedState, rememberFavorite]);

  // Fixar aba (salvar como favorito no backend): tela na hora, servidor em seguida.
  const pinTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    applyPinnedState(tab.path, true);
    pinIntentRef.current.set(tab.path, true);
    pinChangedAtRef.current.set(tab.path, (pinChangeSeqRef.current += 1));
    reconcileFavorite(tab.path, tab.label, tabs.length);
  }, [applyPinnedState, reconcileFavorite, tabs]);

  // Desfixar aba (remover favorito do backend)
  const unpinTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) {
      console.error('Tab não encontrado para tabId:', tabId);
      return;
    }
    applyPinnedState(tab.path, false);
    pinIntentRef.current.set(tab.path, false);
    pinChangedAtRef.current.set(tab.path, (pinChangeSeqRef.current += 1));
    reconcileFavorite(tab.path, tab.label, tabs.length);
  }, [applyPinnedState, reconcileFavorite, tabs]);

  const togglePagePin = useCallback((path: string, label: string) => {
    if (!canAccessRoutePath(path, hasPermission, isSuperAdmin)) return;
    const existing = tabs.find((tab) => tab.path === path);
    if (existing?.isPinned) { unpinTab(existing.id); return; }
    if (existing) { pinTab(existing.id); return; }
    const tab = createPlaceholderTab(path, { label, isClosable: true, isPinned: true, lastAccessed: new Date() });
    setWorkspaceTabs((prev) => {
      const updated = upsertWorkspaceTab(prev, tab);
      return forgetRemovedTabs(prev, limitOpenTabs(updated, path));
    });
    pinIntentRef.current.set(path, true);
    pinChangedAtRef.current.set(path, (pinChangeSeqRef.current += 1));
    reconcileFavorite(path, label, tabs.length);
  }, [hasPermission, isSuperAdmin, pinTab, reconcileFavorite, tabs, unpinTab]);

  // Reordenar abas (arrastar). A ordem local vai junto com o estado salvo das abas; a das
  // fixadas também vai ao servidor (ver createPinnedOrderSync: em série, sem lista
  // parcial e sem desfazer uma ordem mais nova).
  const syncPinnedOrderRef = useRef<((paths: string[]) => void) | null>(null);
  if (!syncPinnedOrderRef.current) {
    const syncSession = sessionRef.current;
    const isCurrentSession = () => syncSession === sessionRef.current;
    syncPinnedOrderRef.current = createPinnedOrderSync({
      getKnownIds: () => new Map(
        Array.from(favoriteTabsMapRef.current.values()).map((favorite) => [favorite.path, favorite.id]),
      ),
      getPendingSync: (path) => favoriteSyncRef.current.get(path),
      fetchFavorites: () => {
        const requestedAt = pinChangeSeqRef.current;
        return favoriteTabsService.getFavorites().then((favorites) => {
          remoteRequestedAtRef.current.set(favorites, requestedAt);
          return favorites;
        });
      },
      rememberFavorites: (favorites) => {
        if (!isCurrentSession()) return;
        const requestedAt = remoteRequestedAtRef.current.get(favorites) ?? -1;
        const { map } = mergeRemoteFavorites(favorites as UserFavoriteTab[], requestedAt);
        favoriteTabsMapRef.current = map;
        setFavoriteTabsMap(map);
      },
      // Outra sessão: não envia a ordem do usuário anterior com a autenticação do novo.
      reorder: (ids) => (isCurrentSession() ? favoriteTabsService.reorderFavorites(ids) : Promise.resolve()),
      restoreServerOrder: (paths) => {
        if (isCurrentSession()) setWorkspaceTabs((prev) => applyPinnedOrder(prev, paths));
      },
      onError: (error) => console.error('Erro ao reordenar favoritos:', error),
    });
  }

  const moveTab = useCallback((tabId: string, overTabId: string) => {
    const from = tabs.findIndex((tab) => tab.id === tabId);
    const to = tabs.findIndex((tab) => tab.id === overTabId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...tabs];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setWorkspaceTabs(next);

    const pinnedPaths = next.filter((tab) => tab.isPinned).map((tab) => tab.path);
    if (moved.isPinned) syncPinnedOrderRef.current?.(pinnedPaths);
  }, [tabs]);

  // Navegar para aba
  const navigateToTab = useCallback((tabId: string) => {
    const tab = tabs.find(tab => tab.id === tabId);
    if (tab) {
      if (!canAccessRoutePath(tab.path, hasPermission, isSuperAdmin)) {
        removeTabByPath(tab.path);
        if (location.pathname !== defaultAccessiblePath) {
          navigate(defaultAccessiblePath);
        }
        return;
      }
      // Log removido - estava causando poluição no console
      // // console.log('Navegando para aba:', tab);
      // A sincronização da rota abaixo é a fonte única para activeTabId e
      // lastAccessed. Atualizar aqui também causava dois commits por troca.
      navigate(tab.path === '/manual' ? buildManualPath() : tab.path);
    }
  }, [
    tabs,
    hasPermission,
    isSuperAdmin,
    removeTabByPath,
    location.pathname,
    defaultAccessiblePath,
    navigate,
  ]);

  const canAccessPath = useCallback(
    (path: string) => canAccessRoutePath(path, (permission) => permissions.includes(permission), isSuperAdmin),
    [permissions, isSuperAdmin]
  );

  // Bootstrap: restaura workspace local, opcionalmente aguarda favoritos em rotas neutras e resolve rota de entrada.
  useEffect(() => {
    if (!user?.id) {
      setIsBootstrapping(false);
      setIsBootstrapped(false);
      setResolvedEntryPath(null);
      return;
    }

    if (permissionsLoading || hasHydratedWorkspaceRef.current) {
      return;
    }

    let cancelled = false;

    const runBootstrap = async () => {
      const persistedState = parsePersistedTabState(localStorage.getItem(TAB_STORAGE_KEY));
      let restoredTabs = dedupeTabsByPath(
        (persistedState?.tabs ?? [])
          .map(fromPersistedTab)
          .filter((tab) => canAccessPath(tab.path)),
      );
      const restoredActivePath = resolveActiveTabId(restoredTabs, persistedState?.activeTabId ?? null);
      const allowFavoritesEntry = shouldAwaitFavoritesForEntry(location.pathname);
      let normalizedFavorites: UserFavoriteTab[] = [];

      if (allowFavoritesEntry) {
        setFavoritesStatus('loading');
        try {
          normalizedFavorites = await fetchNormalizedFavorites(FAVORITES_ENTRY_BOOT_TIMEOUT_MS);
          if (cancelled) {
            return;
          }
          setFavoritesStatus('success');
          favoritesRetryCountRef.current = 0;
          if (favoritesRetryTimeoutRef.current !== null) {
            window.clearTimeout(favoritesRetryTimeoutRef.current);
            favoritesRetryTimeoutRef.current = null;
          }
          hasSyncedFavoritesRef.current = true;
        } catch (error: unknown) {
          if (cancelled) {
            return;
          }
          console.error('Erro ao carregar favoritos no boot de entrada:', error);

          const isTimeoutError = error instanceof Error && error.message === 'FAVORITES_LOAD_TIMEOUT';
          const isUnauthorizedError =
            typeof error === 'object' &&
            error !== null &&
            ('response' in error || 'status' in error) &&
            (((error as { response?: { status?: number } }).response?.status) === 401 ||
              ((error as { status?: number }).status) === 401);

          setFavoritesStatus(isTimeoutError ? 'timeout' : 'error');

          if (isUnauthorizedError) {
            hasFailedAuthRef.current = true;
          }
          hasSyncedFavoritesRef.current = true;
        }
      }

      if (cancelled) {
        return;
      }

      const entryPath = resolveWorkspaceEntryPath({
        pathname: location.pathname,
        favorites: normalizedFavorites,
        restoredActivePath,
        defaultAccessiblePath,
        canAccessPath,
        allowFavoritesEntry,
      });

      let finalTabs = restoredTabs;

      if (isWorkspaceRoute(location.pathname) && canAccessPath(location.pathname)) {
        const existingRouteTab = restoredTabs.find((tab) => tab.path === location.pathname);
        finalTabs = upsertWorkspaceTab(
          restoredTabs,
          createPlaceholderTab(location.pathname, {
            label: existingRouteTab?.label,
            isPinned: existingRouteTab?.isPinned,
            isClosable: existingRouteTab?.isClosable,
            lastAccessed: existingRouteTab?.lastAccessed,
          }),
        );
      } else {
        if (isWorkspaceRoute(location.pathname) && !canAccessPath(location.pathname)) {
          finalTabs = finalTabs.filter((tab) => tab.path !== location.pathname);
        }

        if (normalizedFavorites.length > 0) {
          finalTabs = reconcileWorkspaceWithRemoteFavorites(finalTabs, normalizedFavorites);
        }

        const entryFavorite = normalizedFavorites.find((favorite) => favorite.path === entryPath);
        const existingEntryTab = finalTabs.find((tab) => tab.path === entryPath);
        finalTabs = upsertWorkspaceTab(
          finalTabs,
          createPlaceholderTab(entryPath, {
            label: entryFavorite?.label ?? existingEntryTab?.label,
            isPinned: Boolean(entryFavorite) || existingEntryTab?.isPinned,
            isClosable: existingEntryTab?.isClosable,
            lastAccessed: existingEntryTab?.lastAccessed,
          }),
        );

        if (isWorkspaceRoute(location.pathname) && location.pathname !== entryPath) {
          navigate(entryPath, { replace: true });
        }
      }

      if (normalizedFavorites.length > 0) {
        const favoritesMap = new Map<string, UserFavoriteTab>();
        normalizedFavorites.forEach((favorite) => {
          favoritesMap.set(favorite.path, favorite);
        });
        setFavoriteTabsMap(favoritesMap);
      }

      setWorkspaceTabs(limitOpenTabs(finalTabs, entryPath));
      setActiveTabId(entryPath);
      setResolvedEntryPath(entryPath);
      entryPathCommittedRef.current = true;
      hasHydratedWorkspaceRef.current = true;
      setIsBootstrapping(false);
      setIsBootstrapped(true);
    };

    void runBootstrap();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    permissionsLoading,
    location.pathname,
    canAccessPath,
    defaultAccessiblePath,
    navigate,
    fetchNormalizedFavorites,
  ]);

  // Sincronização contínua da rota atual com o workspace restaurado.
  useEffect(() => {
    if (!user?.id || permissionsLoading || !isBootstrapped || !hasHydratedWorkspaceRef.current) {
      return;
    }

    if (!isWorkspaceRoute(location.pathname)) {
      return;
    }

    if (!canAccessPath(location.pathname)) {
      removeTabByPath(location.pathname);
      if (location.pathname !== defaultAccessiblePath) {
        navigate(defaultAccessiblePath, { replace: true });
      }
      return;
    }

    setWorkspaceTabs((prev) => {
      const existingRouteTab = prev.find((tab) => tab.path === location.pathname);
      const updated = upsertWorkspaceTab(
        prev,
        createPlaceholderTab(location.pathname, {
          label: existingRouteTab?.label,
          // Favorito que o usuário acabou de desfixar (DELETE em voo) não volta a fixar.
          isPinned: existingRouteTab?.isPinned
            || (favoriteTabsMap.has(location.pathname) && pinIntentRef.current.get(location.pathname) !== false),
          isClosable: existingRouteTab?.isClosable,
          lastAccessed: new Date(),
        })
      );
      return forgetRemovedTabs(prev, limitOpenTabs(updated, location.pathname));
    });
    setActiveTabId(location.pathname);
  }, [
    user?.id,
    permissionsLoading,
    isBootstrapped,
    location.pathname,
    canAccessPath,
    favoriteTabsMap,
    removeTabByPath,
    defaultAccessiblePath,
    navigate,
  ]);

  // Favoritos remotos sincronizam em background sem bloquear o boot local.
  useEffect(() => {
    if (!user?.id || permissionsLoading || !isBootstrapped) {
      return;
    }

    if (hasSyncedFavoritesRef.current || hasFailedAuthRef.current) {
      return;
    }

    const loadFavorites = async () => {
      if (isFetchingFavoritesRef.current) return;
      isFetchingFavoritesRef.current = true;
      const session = sessionRef.current;
      const requestedAt = pinChangeSeqRef.current;
      setFavoritesStatus('loading');
      
      try {
        const normalizedFavorites = await fetchNormalizedFavorites(FAVORITES_LOAD_TIMEOUT_MS);
        // Resposta de outra sessão: não toca em estado, status nem indicadores de sync.
        if (session !== sessionRef.current) return;

        applyFavoritesToWorkspace(normalizedFavorites, requestedAt);
        setFavoritesStatus('success');
        favoritesRetryCountRef.current = 0;
        if (favoritesRetryTimeoutRef.current !== null) {
          window.clearTimeout(favoritesRetryTimeoutRef.current);
          favoritesRetryTimeoutRef.current = null;
        }
        hasSyncedFavoritesRef.current = true;
      } catch (error: unknown) {
        if (session !== sessionRef.current) return;
        console.error('Erro ao carregar favoritos:', error);

        const hasLocalPinnedTabs = workspaceTabs.some((tab) => tab.isPinned);
        const isTimeoutError = error instanceof Error && error.message === 'FAVORITES_LOAD_TIMEOUT';
        const isUnauthorizedError =
          typeof error === 'object' &&
          error !== null &&
          ('response' in error || 'status' in error) &&
          (((error as { response?: { status?: number } }).response?.status) === 401 ||
            ((error as { status?: number }).status) === 401);
        const status: FavoritesStatus = isTimeoutError
          ? (hasLocalPinnedTabs ? 'stale' : 'timeout')
          : (hasLocalPinnedTabs ? 'stale' : 'error');

        setFavoritesStatus(status);

        if (isUnauthorizedError) {
          hasFailedAuthRef.current = true;
        }
        hasSyncedFavoritesRef.current = true;

        if (!isUnauthorizedError && favoritesRetryCountRef.current < MAX_FAVORITES_RETRIES) {
          favoritesRetryCountRef.current += 1;
          if (favoritesRetryTimeoutRef.current !== null) {
            window.clearTimeout(favoritesRetryTimeoutRef.current);
          }
          favoritesRetryTimeoutRef.current = window.setTimeout(() => {
            hasSyncedFavoritesRef.current = false;
            setFavoritesRetryNonce((value) => value + 1);
          }, FAVORITES_RETRY_DELAY_MS);
        }
      } finally {
        if (session === sessionRef.current) isFetchingFavoritesRef.current = false;
      }
    };

    void loadFavorites();
  }, [
    user?.id,
    permissionsLoading,
    isBootstrapped,
    canAccessFavoritePath,
    workspaceTabs,
    favoritesRetryNonce,
    fetchNormalizedFavorites,
    applyFavoritesToWorkspace,
  ]);

  // Salvar estado da aba (mantido para compatibilidade)
  const saveTabState = useCallback((tabId: string, state: any) => {
    setWorkspaceTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, data: state } : tab
    ));
  }, []);

  // Obter estado da aba (mantido para compatibilidade)
  const getTabState = useCallback((tabId: string) => {
    const tab = tabs.find(tab => tab.id === tabId);
    return tab?.data;
  }, [tabs]);

  // === SISTEMA DE CACHE DE DADOS ===

  // Definir dados da aba
  const setTabData = useCallback((tabId: string, data: any) => {
    setWorkspaceTabs(prev => prev.map(tab => {
      if (tab.id === tabId) {
        return { 
          ...tab, 
          data, 
          lastDataUpdate: new Date(),
          isLoading: false,
          error: null,
          isDirty: false
        };
      }
      return tab;
    }));
    
    // Limpar dados de outras tabs se necessário
    if (tabId === activeTabId) {
      cleanupInactiveTabData();
    }
  }, [activeTabId, cleanupInactiveTabData]);

  // Obter dados da aba
  const getTabData = useCallback((tabId: string) => {
    const tab = tabs.find(tab => tab.id === tabId);
    return tab?.data;
  }, [tabs]);

  // Definir estado de carregamento
  const setTabLoading = useCallback((tabId: string, isLoading: boolean) => {
    setWorkspaceTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, isLoading } : tab
    ));
  }, []);

  // Definir erro da aba
  const setTabError = useCallback((tabId: string, error: string | null) => {
    setWorkspaceTabs(prev => prev.map(tab => 
      tab.id === tabId ? { 
        ...tab, 
        error, 
        isLoading: false 
      } : tab
    ));
  }, []);

  // Marcar aba como modificada (precisa recarregar)
  const markTabAsDirty = useCallback((tabId: string) => {
    setWorkspaceTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, isDirty: true } : tab
    ));
  }, []);

  // Limpar cache da aba
  const clearTabCache = useCallback((tabId: string) => {
    setWorkspaceTabs(prev => prev.map(tab => 
      tab.id === tabId ? { 
        ...tab, 
        data: undefined, 
        lastDataUpdate: undefined,
        error: null,
        isDirty: false
      } : tab
    ));
  }, []);

  // Recarregar dados da aba (força recarregamento)
  const refreshTabData = useCallback((tabId: string) => {
    const tab = tabs.find((item) => item.id === tabId);
    if (tab && !confirmTabClosures([tab.path])) return;
    setWorkspaceTabs(prev => prev.map(tab =>
      tab.id === tabId ? { 
        ...tab, 
        isDirty: true,
        error: null,
        refreshKey: (tab.refreshKey ?? 0) + 1,
      } : tab
    ));
  }, [tabs]);

  const preferredInitialPath = resolvedEntryPath ?? defaultAccessiblePath;

  const value = useMemo<TabManagerContextType>(() => ({
    tabs,
    activeTabId,
    isBootstrapping,
    isBootstrapped,
    favoritesStatus,
    preferredInitialPath,
    openTab,
    closeTab,
    removeTabByPath,
    pruneInaccessibleTabs,
    closeTabAndNavigate,
    activateTab,
    closeOtherTabs,
    closeAllTabs,
    pinTab,
    unpinTab,
    togglePagePin,
    moveTab,
    navigateToTab,
    saveTabState,
    getTabState,
    setActiveTabId,
    updateTabLabelForPath,
    setTabData,
    getTabData,
    setTabLoading,
    setTabError,
    markTabAsDirty,
    clearTabCache,
    refreshTabData,
  }), [
    tabs,
    activeTabId,
    isBootstrapping,
    isBootstrapped,
    favoritesStatus,
    preferredInitialPath,
    openTab,
    closeTab,
    removeTabByPath,
    pruneInaccessibleTabs,
    closeTabAndNavigate,
    activateTab,
    closeOtherTabs,
    closeAllTabs,
    pinTab,
    unpinTab,
    togglePagePin,
    moveTab,
    navigateToTab,
    saveTabState,
    getTabState,
    updateTabLabelForPath,
    setTabData,
    getTabData,
    setTabLoading,
    setTabError,
    markTabAsDirty,
    clearTabCache,
    refreshTabData,
  ]);

  return (
    <TabManagerContext.Provider value={value}>
      {children}
    </TabManagerContext.Provider>
  );
};
